// Download ciphertext from presigned URL and decrypt to a display Blob.

import { webMediaBlobCache } from '@core/persistence/db';

import {
  type BlobRefWire,
  decryptMediaBlob,
  parseBlobRefWire,
} from './mediaCrypto';
import { downloadCiphertextBlob, fetchMediaDownloadUrl } from './mediaUpload';

export async function fetchAndDecryptMediaBlob(
  blobRef: BlobRefWire,
  signal?: AbortSignal,
  mime = 'image/jpeg',
  onProgress?: (fraction: number) => void,
): Promise<Blob> {
  const { mediaId, contentKey, contentIv } = parseBlobRefWire(blobRef);

  // Offline-first: a previously-decrypted blob is served straight from the
  // local cache — instant, and works with no network. Key is the stable
  // mediaId, so the same attachment never re-downloads/re-decrypts. Skip the
  // cache read when a progress callback is wired (an in-flight download UI
  // wants to animate); the result is still cached at the end.
  if (onProgress === undefined) {
    const cached = await webMediaBlobCache.get(mediaId);
    if (cached !== undefined) return cached;
  }

  const downloadUrl = await fetchMediaDownloadUrl(mediaId);
  const ciphertext = await downloadCiphertextBlob(downloadUrl, signal, onProgress);
  const plaintext = await decryptMediaBlob(ciphertext, contentKey, contentIv);
  const ab = new ArrayBuffer(plaintext.byteLength);
  new Uint8Array(ab).set(plaintext);
  const blob = new Blob([ab], { type: mime });
  // Persist for instant + offline re-render next time (best-effort).
  void webMediaBlobCache.put(mediaId, blob, mime);
  return blob;
}

export function blobUrlFromPlaintext(plaintext: Uint8Array, mime: string): string {
  const ab = new ArrayBuffer(plaintext.byteLength);
  new Uint8Array(ab).set(plaintext);
  return URL.createObjectURL(new Blob([ab], { type: mime }));
}
